import React, { ChangeEvent, useState, useEffect, useContext } from "react";
import UserContext from "../../../UserContext";
import useChangeTracker from "../../../hooks/change-tracker/useChangeTracker";
import useSnackbar from "../../../hooks/snackbar/useSnackbar";
import TrSnackbar from "../../shared/controls/TrSnackbar";
import StudentDataItem from "./StudentDataItem";
import StepHandler from "../../../models/shared/StepHandler";
import SelectItem from "../../../models/shared/SelectItem";
import { TransportOptionType } from "../../../models/transport-option-type/TransportOptionType";
import { VehicleType } from "../../../models/vehicle-type/VehicleType";
import FinancingRequestStudent from "../../../models/financing-request/FinancingRequestStudent";
import FinancingRequestStudentData from "../../../models/financing-request/student-data/FinancingRequestStudentData";
import { getValidationConfigurations } from "../../../models/financing-request/student-data/FinancingRequestStudentDataValidation";
import TrackerName from "../../../models/financing-request/student-data/FinancingRequestStudentDataTrackerName";
import { SchoolCarrier } from "../../../models/financing-request/SchoolCarrier";
import { FoodOptionInital } from "../../../models/financing-request/FoodOption";
import { ProgramType } from "../../../models/financing-request/ProgramType";
import SchoolCarrierTransportOption, {
  SchoolCarrierTransportOptionInital
} from "../../../models/financing-request/transport-option/SchoolCarrierTransportOption";
import PersonalTransportOption, {
  PersonalTransportOptionInital
} from "../../../models/financing-request/transport-option/PersonalTransportOption";
import { TransportOption } from "../../../models/financing-request/transport-option/TransportOption";
import PersonalPassengerTransportOption from "../../../models/financing-request/transport-option/PersonalPassengerTransportOption";
import { AuthorizedCarrierTransportOptionInital } from "../../../models/financing-request/transport-option/AuthorizedCarrierTransportOption";
import { CompanionInPublicTransportOptionInital } from "../../../models/financing-request/transport-option/CompanionInPublicTransportOption";
import ElementarySchoolTeachingAidOption, {
  ElementarySchoolTeachingAidOptionInital
} from "../../../models/financing-request/teaching-aid-option/ElementarySchoolTeachingAidOption";
import HighSchoolTeachingAidOption, {
  HighSchoolTeachingAidOptionInital
} from "../../../models/financing-request/teaching-aid-option/HighSchoolTeachingAidOption";
import useCodeBook from "../../../hooks/codebook/useCodeBook";
import useStudentDataInfo from "../../../hooks/student-data-info/useStudentDataInfo";
import { nameof } from "../../../utilities/utilities";
import { AuthorizedCarrier } from "../../../models/financing-request/AuthorizedCarrier";
import Currency from "../../../models/shared/Currency";
import AnnualAdministration from "../../../models/admin/annual-administration/AnnualAdministration";
import PaymentRequestApiClient from "../../../api/PaymentRequestApiClient";

interface Props extends StepHandler {
  schoolCarriers: SchoolCarrier[];
  authorizedCarriers: AuthorizedCarrier[];
  students: FinancingRequestStudent[];
  studentsDataState: FinancingRequestStudentData[];
  currency: Currency;
  annualPricing: AnnualAdministration;
  updateStudentsData: (studentsData: FinancingRequestStudentData[]) => void;
}

export interface PersonalPassenger {
  passengerId: number;
  personal: FinancingRequestStudent;
}

export default function FinancingRequestStudentDataStep({
  buttons,
  onPrevious,
  onNext,
  goToPreviousStep,
  goToNextStep,
  onStepperSave,
  schoolCarriers,
  authorizedCarriers,
  students,
  studentsDataState,
  currency,
  annualPricing,
  updateStudentsData
}: Props) {
  const { user } = useContext(UserContext);

  const [studentsData, setStudentsData] = useState<
    FinancingRequestStudentData[]
  >(new Array<FinancingRequestStudentData>());

  const {
    studentDataInfoReady,
    getTransportOptionDataInfo,
    getFoodOptionDataInfo,
    getTeachingAidOptionDataInfo
  } = useStudentDataInfo({});

  useEffect(() => {

    if(annualPricing !== null && annualPricing.elementarySchoolTeachingAidPrice== undefined)
    {
      console.log("FinancingRequestStudentDataStep - no price");
    }
    async function getStudents() {
      if (studentsDataState.length == 0) {
        let newStudentsData = new Array<FinancingRequestStudentData>();
        students.forEach(student => {
          if (student.isSelected) {
            newStudentsData.push({
              student: student,
              currency: currency              
            });
          }
        });

        setStudentsData(newStudentsData);
      } else {
        setStudentsData(
          studentsDataState.map(studentData => {
            var data = { ...studentData };

            if (data.transportOption) {
              data.transportOptionInfo = getTransportOptionDataInfo(
                data.transportOption,
                currency,
                schoolCarriers,
                authorizedCarriers                
              );
            }
            if (data.foodOption) {
              data.foodOptionInfo = getFoodOptionDataInfo(data.foodOption);
            }
            if (data.teachingAidOption) {
              data.teachingAidOptionInfo = getTeachingAidOptionDataInfo(
                data.teachingAidOption,
                data.student.programType,
                data.student.specialNeedType,
                currency,
                annualPricing
              );
            }

            return data;
          })
        );
      }
    }

    if (studentDataInfoReady) {
      getStudents();
    }
  }, [studentDataInfoReady, studentsDataState]);

  useEffect(() => {
    return updateTrackerDebounceEffect([
      [TrackerName.StudentsData, studentsData]
    ]);
  }, [studentsData]);

  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    message: snackbarMessage,
    close: closeSnackbar,
    variant: snackbarVariant
  } = useSnackbar();

  const {
    validationResult,
    updateTrackerDebounceEffect,
    showErrors
  } = useChangeTracker(
    getValidationConfigurations(schoolCarriers, authorizedCarriers),
    false
  );

  const handlePreviousStep = () => {
    if (goToPreviousStep != null) {
      updateStudentsData(studentsData);
      goToPreviousStep();
    }
  };

  const handleNextStep = () => {
    if (!validationResult.isValid(false)) {
      showErrors();

      const validationMessages = validationResult
        .getTrackerMessages(TrackerName.StudentsData, false)
        .filter(
          m => m.validationType === "min" || m.validationType === "StudentData"
        );

      let message = "";
      if (validationMessages.length > 0) {
        message = validationMessages[0].message;
      } else {
        message =
          "Ne možete prijeći na sljedeći korak dok ne ispunite sva obavezna polja. Molimo provjerite vrijednosti u crveno označenim poljima.";
      }

      openSnackbar(message, "error");
    } else {
      updateStudentsData(studentsData);
      goToNextStep && goToNextStep();
    }
  };

  function getPersonalPassengers(): PersonalPassenger[] {
    let personalPassengers = new Array<PersonalPassenger>();
    studentsData.forEach(studentData => {
      if (
        studentData.transportOption &&
        studentData.transportOption.transportOptionType &&
        studentData.transportOption.transportOptionType ==
          TransportOptionType.Personal
      ) {
        const personalTransportOption = studentData.transportOption as PersonalTransportOption;
        if (
          personalTransportOption.passengers &&
          personalTransportOption.passengers.length > 0
        ) {
          personalTransportOption.passengers.forEach(passenger => {
            personalPassengers.push({
              passengerId: passenger.id,
              personal: studentData.student
            });
          });
        }
      }
    });

    return personalPassengers;
  }

  function getTransportOptionInital(
    studentData: FinancingRequestStudentData,
    selectValue: TransportOptionType
  ): FinancingRequestStudentData {
    switch (selectValue) {
      case TransportOptionType.SchoolCarrier:
        let studentSchoolCarrier = {
          ...studentData,
          transportOption: {
            transportOptionType: selectValue,
            ...SchoolCarrierTransportOptionInital
          }
        };
        studentSchoolCarrier.transportOptionInfo = getTransportOptionDataInfo(
          studentSchoolCarrier.transportOption,
          currency,
          schoolCarriers,
          authorizedCarriers          
        );
        return studentSchoolCarrier;
      case TransportOptionType.Personal:
        let studentPersonal = {
          ...studentData,
          transportOption: {
            transportOptionType: selectValue,
            ...PersonalTransportOptionInital
          }
        };
        studentPersonal.transportOptionInfo = getTransportOptionDataInfo(
          studentPersonal.transportOption,
          currency,
          schoolCarriers,
          authorizedCarriers          
        );
        return studentPersonal;
      case TransportOptionType.AuthorizedCarrier:
        let studentAuthorizedCarrier = {
          ...studentData,
          transportOption: {
            transportOptionType: selectValue,
            ...AuthorizedCarrierTransportOptionInital
          }
        };
        studentAuthorizedCarrier.transportOptionInfo = getTransportOptionDataInfo(
          studentAuthorizedCarrier.transportOption,
          currency,
          schoolCarriers,
          authorizedCarriers          
        );
        return studentAuthorizedCarrier;
      case TransportOptionType.CompanionInPublicTransport:
        let studentCompanionInPublicTransport = {
          ...studentData,
          transportOption: {
            transportOptionType: selectValue,
            ...CompanionInPublicTransportOptionInital
          }
        };
        studentCompanionInPublicTransport.transportOptionInfo = getTransportOptionDataInfo(
          studentCompanionInPublicTransport.transportOption,
          currency,
          schoolCarriers,
          authorizedCarriers          
        );
        return studentCompanionInPublicTransport;
      default:
        return studentData;
    }
  }

  const handleTransportTypeChange = (studentId: number) => (
    e: React.ChangeEvent<{ name?: string | undefined; value: any }>
  ) => {
    const { value } = e.target;
    const currentStudent = studentsData.find(sd => sd.student.id == studentId);
    let drivingStudents = new Array<FinancingRequestStudent>();
    if (currentStudent) {
      if (
        currentStudent.transportOption &&
        currentStudent.transportOption.transportOptionType &&
        currentStudent.transportOption.transportOptionType ==
          TransportOptionType.Personal &&
        value != TransportOptionType.Personal
      ) {
        const personalTransportOption = currentStudent.transportOption as PersonalTransportOption;
        if (personalTransportOption.passengers) {
          personalTransportOption.passengers.forEach(passenger => {
            drivingStudents.push(passenger);
          });
        }
      }
    }

    setStudentsData(prevStudentsData =>
      prevStudentsData.map(studentData => {
        if (studentData.student.id == studentId) {
          return getTransportOptionInital(studentData, value);
        } else if (
          drivingStudents.filter(ds => ds.id == studentData.student.id).length >
          0
        ) {
          let drivingStudent = {
            ...studentData,
            transportOption: undefined
          };
          drivingStudent.transportOptionInfo = "";
          return drivingStudent;
        } else {
          return studentData;
        }
      })
    );
  };

  const handleTransportSelectChange = (studentId: number) => (
    e: React.ChangeEvent<{ name: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    setStudentsData(prevStudentsData =>
      prevStudentsData.map(studentData => {
        if (studentData.student.id == studentId) {
          if (
            studentData.transportOption &&
            studentData.transportOption.hasOwnProperty(
              nameof<SchoolCarrier>("vehicleType")
            ) &&
            nameof<SchoolCarrier>("vehicleType") === name
          ) {
            let student = {
              ...studentData,
              transportOption: {
                ...studentData.transportOption,
                [name]: value,
                registrationPlate: undefined
              }
            };

            student.transportOptionInfo = getTransportOptionDataInfo(
              student.transportOption,
              currency,
              schoolCarriers,
              authorizedCarriers
            );
            return student;
          } else {
            let student = {
              ...studentData,
              transportOption: { ...studentData.transportOption, [name]: value }
            };
            student.transportOptionInfo = getTransportOptionDataInfo(
              student.transportOption,
              currency,
              schoolCarriers,
              authorizedCarriers              
            );
            return student;
          }
        }
        return studentData;
      })
    );
  };

  const updatePassengers = (
    studentData: FinancingRequestStudentData,
    passengersIds?: number[]
  ) => {
    if (
      studentData.transportOption &&
      studentData.transportOption.transportOptionType &&
      studentData.transportOption.transportOptionType ==
        TransportOptionType.Personal
    ) {
      const personalTransport = studentData.transportOption as PersonalTransportOption;
      if (passengersIds == null && personalTransport.passengers) {
        let ids = new Array<number>();
        personalTransport.passengers.forEach(passenger => {
          ids.push(passenger.id);
        });

        passengersIds = ids;
      }
      const personalPassengers = getPersonalPassengers().filter(
        personalPassenger =>
          personalPassenger.personal.id == studentData.student.id
      );

      setStudentsData(prevStudentsData =>
        prevStudentsData.map(prevStudentData => {
          //update currently selected
          if (
            passengersIds &&
            passengersIds.includes(prevStudentData.student.id)
          ) {
            let student = {
              ...prevStudentData,
              transportOption: {
                transportOptionType: TransportOptionType.PersonalPassenger,
                personalStudent: studentData.student
              }
            };
            student.transportOptionInfo = getTransportOptionDataInfo(
              student.transportOption,
              currency,
              schoolCarriers,
              authorizedCarriers              
            );
            return student;
          }
          //update deselected

          return prevStudentData;
        })
      );
    }
  };

  const handleTransportSelectChangeMultipleStudents = (studentId: number) => (
    e: React.ChangeEvent<{ name: string; value: number[] }>
  ) => {
    const { name, value } = e.target;
    let selctedStudents = new Array<FinancingRequestStudent>();
    students.forEach(student => {
      value.forEach(v => {
        if (v == student.id) selctedStudents.push(student);
      });
    });

    const currentStudent = studentsData.find(
      data => data.student.id == studentId
    );

    setStudentsData(prevStudentsData =>
      prevStudentsData.map(studentData => {
        const selectedStudent = selctedStudents.find(
          selected => selected.id == studentData.student.id
        );
        //update currently selected passengers
        if (selectedStudent) {
          let student = {
            ...studentData,
            transportOption: {
              id: studentData.transportOption
                ? studentData.transportOption.id
                : undefined,
                currency: studentData.currency,
              transportOptionType: TransportOptionType.PersonalPassenger,
              personalStudent: currentStudent ? currentStudent.student : null              
            }
          };
          student.transportOptionInfo = getTransportOptionDataInfo(
            student.transportOption,
            student.currency,
            schoolCarriers,
            authorizedCarriers            
          );
          return student;
        }
        //update deselected passengers
        else if (
          !selectedStudent &&
          studentData.transportOption &&
          studentData.transportOption.transportOptionType &&
          studentData.transportOption.transportOptionType ==
            TransportOptionType.PersonalPassenger
        ) {
          const personalPassenger = getPersonalPassengers().find(
            personalPassenger =>
              personalPassenger.passengerId == studentData.student.id
          );
          if (
            !personalPassenger ||
            personalPassenger.personal.id == studentId
          ) {
            let student = {
              ...studentData,
              transportOption: undefined
            };
            student.transportOptionInfo = "-";
            return student;
          }
        }
        //update current personal student
        else if (studentData.student.id == studentId) {
          let student = {
            ...studentData,
            transportOption: {
              ...studentData.transportOption,
              passengers: selctedStudents,
              currency: studentData.currency,
            }
          };
          student.transportOptionInfo = getTransportOptionDataInfo(
            student.transportOption,
            student.currency,
            schoolCarriers,
            authorizedCarriers            
          );
          return student;
        }

        return studentData;
      })
    );
  };

  const handleTransportChange = (studentId: number) => (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value, type } = e.target;
    setStudentsData(prevStudentsData =>
      prevStudentsData.map(prevStudentData => {
        if (prevStudentData.student.id == studentId) {
          let student = {
            ...prevStudentData,
            transportOption: {
              ...prevStudentData.transportOption,
              [name]: type === "number" ? +value : value
            }
          };
          student.transportOptionInfo = getTransportOptionDataInfo(
            student.transportOption,
            student.currency,
            schoolCarriers,
            authorizedCarriers            
          );
          return student;
        }
        return prevStudentData;
      })
    );
  };

  const handleAddTransportOption = (studentId: number) => (e: any) => {
    setStudentsData(prevStudentsData =>
      prevStudentsData.map(studentData => {
        if (studentData.student.id == studentId) {
          return {
            ...studentData,
            transportOption: {
              transportOptionType: null
            } as TransportOption
          };
        }
        return studentData;
      })
    );
  };

  const handleDeleteTransportOption = (studentId: number) => (e: any) => {
    const currentStudent = studentsData.find(
      data => data.student.id == studentId
    );
    let passengerStudents = new Array<FinancingRequestStudent>();

    if (
      currentStudent &&
      currentStudent.transportOption &&
      currentStudent.transportOption.transportOptionType &&
      currentStudent.transportOption.transportOptionType ==
        TransportOptionType.Personal
    ) {
      const currentStudentPassengerStudents = (currentStudent.transportOption as PersonalTransportOption)
        .passengers;
      if (
        currentStudentPassengerStudents &&
        currentStudentPassengerStudents.length > 0
      ) {
        currentStudentPassengerStudents.forEach(passengerStudent => {
          passengerStudents.push(passengerStudent);
        });
      }
    }

    setStudentsData(prevStudentsData =>
      prevStudentsData.map(studentData => {
        if (studentData.student.id == studentId) {
          let student = {
            ...studentData,
            transportOption: undefined
          };
          student.transportOptionInfo = "-";
          return student;
        } else if (
          passengerStudents.length > 0 &&
          passengerStudents.find(
            passengerStudent => passengerStudent.id == studentData.student.id
          ) != null
        ) {
          let student = {
            ...studentData,
            transportOption: undefined
          };
          student.transportOptionInfo = "-";
          return student;
        }
        return studentData;
      })
    );
  };

  const handleAddFoodOption = (studentId: number) => (e: any) => {
    setStudentsData(prevStudentsData =>
      prevStudentsData.map(prevStudentData => {
        if (prevStudentData.student.id == studentId) {
          return {
            ...prevStudentData,
            foodOption: FoodOptionInital
          };
        }
        return prevStudentData;
      })
    );
  };

  const handleDeleteFoodOption = (studentId: number) => (e: any) => {
    setStudentsData(prevStudentsData =>
      prevStudentsData.map(prevStudentData => {
        if (prevStudentData.student.id == studentId) {
          let student = {
            ...prevStudentData,
            foodOption: undefined
          };

          student.foodOptionInfo = "-";
          return student;
        }
        return prevStudentData;
      })
    );
  };

  const handleFoodChange = (studentId: number) => (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = e.target;
    setStudentsData(prevStudentsData =>
      prevStudentsData.map(prevStudentData => {
        if (prevStudentData.student.id == studentId) {
          let student = {
            ...prevStudentData,
            foodOption: {
              ...prevStudentData.foodOption,
              [name]: checked
            }
          };
          student.foodOptionInfo = getFoodOptionDataInfo(student.foodOption);
          return student;
        }
        return prevStudentData;
      })
    );
  };

  function getTeachingAidOptionInital(
    studentData: FinancingRequestStudentData
  ): FinancingRequestStudentData {
    switch (studentData.student.programType) {
      case ProgramType.Elementary:
        return {
          ...studentData,
          teachingAidOption: {
            ...ElementarySchoolTeachingAidOptionInital
          }
        };
      case ProgramType.High:
        return {
          ...studentData,
          teachingAidOption: {
            ...HighSchoolTeachingAidOptionInital
          }
        };
      default:
        return studentData;
    }
  }

  const handleTeachingAidChange = (studentId: number) => (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = e.target;
    setStudentsData(prevStudentsData =>
      prevStudentsData.map(prevStudentData => {
        if (prevStudentData.student.id == studentId) {
          let student = {
            ...prevStudentData,
            teachingAidOption: {
              ...prevStudentData.teachingAidOption,
              [name]: checked
            }
          };
          student.teachingAidOptionInfo = getTeachingAidOptionDataInfo(
            student.teachingAidOption,
            student.student.programType,
            student.student.specialNeedType,
            student.currency,
            annualPricing
          );
          return student;
        }
        return prevStudentData;
      })
    );
  };

  const handleAddTeachingAidOption = (studentId: number) => (e: any) => {
    setStudentsData(prevStudentsData =>
      prevStudentsData.map(prevStudentData => {
        if (prevStudentData.student.id == studentId) {
          return getTeachingAidOptionInital(prevStudentData);
        }
        return prevStudentData;
      })
    );
  };

  const handleDeletTeachingAidOption = (studentId: number) => (e: any) => {
    setStudentsData(prevStudentsData =>
      prevStudentsData.map(prevStudentData => {
        if (prevStudentData.student.id == studentId) {
          let student = {
            ...prevStudentData,
            teachingAidOption: undefined
          };

          student.teachingAidOptionInfo = "-";
          return student;
        }
        return prevStudentData;
      })
    );
  };

  const handleUpdateState = () => {
    updateStudentsData(studentsData);
  };

  onPrevious && onPrevious(handlePreviousStep);
  onNext && onNext(handleNextStep);
  onStepperSave && onStepperSave(handleUpdateState);

  return (
    <>
      <div className="studentsDataWrapper">
        {studentsData.map((studentData, index) => (
          <StudentDataItem
            key={studentData.student.id}
            index={index}
            studentData={studentData}
            studentsData={studentsData}
            personalPassengers={getPersonalPassengers()}
            schoolCarriers={schoolCarriers}
            authorizedCarriers={authorizedCarriers}
            validationResult={validationResult}
            currency={currency}
            // schoolYearName={schoolYearName}
            // schoolYearId={schoolYearId}
            annualPricing={annualPricing}
            onTransportSelectChange={handleTransportSelectChange}
            handleTransportSelectChangeMultipleStudents={
              handleTransportSelectChangeMultipleStudents
            }
            onTransportTypeChange={handleTransportTypeChange}
            onTransportChange={handleTransportChange}
            addTransportOption={handleAddTransportOption}
            deleteTransportOption={handleDeleteTransportOption}
            addFoodOption={handleAddFoodOption}
            deleteFoodOption={handleDeleteFoodOption}
            onFoodChange={handleFoodChange}
            addTeachingAidOption={handleAddTeachingAidOption}
            deleteTeachingAidOption={handleDeletTeachingAidOption}
            onTeachingAidChange={handleTeachingAidChange}
          />
        ))}
      </div>
      {buttons}
      <TrSnackbar
        variant={snackbarVariant}
        isOpened={isSnackbarOpened}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
    </>
  );
}
